import { utcToZonedTime } from "date-fns-tz";
import { isString } from "lodash";
import { EEventSlotStatus, EventSlot } from "../api/EventSlot/EventSlot";
import { EFrequencyPaymentType, EMaintenanceType, ProductSubscription } from "../api/Product/ProductSubscriptionApi";
import {
  EPaymentStatus,
  EPaymentType,
  EProductClassification,
  EProductClassificationDescriptions,
  EQuotePurchaserType,
  EQuoteType,
  Payment
} from "../api/Types";
import { EDeductionType } from "../api/Deduction/EDeductionType";
import { Deduction } from "../api/Deduction/Deduction";
import { EQuoteStatus } from "../api/Quote/EQuoteStatus";
import { QuoteDeduction } from "../api/Quote/QuoteDeduction";
import { QuoteSubscription } from "../api/QuoteSubscription/QuoteSubscription";
import { QuoteLineItem } from "../api/Quote/QuoteLineItem";
import { Quote } from "../api/Quote/Quote";
import {
  BoilerVatRate,
  ScheduleCategory,
  SolarBusinessAgriVatRate,
  SolarVatRate,
  DepositBoiler,
  DepositDefault,
  DepositSolarDomestic,
  DepositSolarNonDomestic
} from "./consts";
import { useEffect, useRef } from "react";
import { SolarReport } from "../api/Quote/SolarReport";
import { Price, Product } from "../api/Product/Product";

export const lineItemSort = function (a: QuoteLineItem, b: QuoteLineItem): number {
  if (a.price <= 0 && b.price <= 0) {
    return a.price * a.quantity - b.price * b.quantity;
  }
  return b.price * b.quantity - a.price * a.quantity;
};

export const lineAddonSort = function (a: QuoteLineItem, b: QuoteLineItem): number {
  if (a.price <= 0 && b.price <= 0) {
    return a.price - b.price;
  }
  return b.price - a.price;
};

export const deductionSort = function (a: QuoteDeduction, b: QuoteDeduction): number {
  return quoteDeductionValue(a) - quoteDeductionValue(b);
};

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

// Quote functions

export const addonClassificationOrder = [
  EProductClassification.Controls,
  EProductClassification.Cylinder,
  EProductClassification.Radiators,
  EProductClassification.Powerflush
];

const addonClassificationSort = function (a: EProductClassification, b: EProductClassification) {
  const heavyWeght = function (item: any) {
    const index = addonClassificationOrder.indexOf(item);
    //todo hardcoded weight
    return index === -1 ? 1000 : index;
  };

  return heavyWeght(a) - heavyWeght(b);
};

export const boilerClassificationOrder = [
  EProductClassification.GasBoilers,
  EProductClassification.OilBoilers,
  EProductClassification.Controls,
  EProductClassification.Cylinder
];

export const solarClassificationOrder = [
  EProductClassification.SolarPV,
  EProductClassification.Battery,
  EProductClassification.Inverter
];

export const quoteDetectSteps = function (
  quote: Quote,
  subscriptions: ProductSubscription[] | null,
  isAvailableSchedule: boolean
): string[] {
  let steps: string[] = [];

  if (isAvailableSchedule) {
    steps.push(ScheduleCategory);
  }

  steps = steps.concat(quoteAddonClassifications(quote));

  if (subscriptions !== null) {
    const subscription = quoteAvailableSubscriptions(quote, subscriptions);

    if (hasSubscriptions(subscription)) {
      steps.push(EProductClassification.Maintenance);
    }
  }

  return steps;
};

export const quoteAvailableSubscriptions = function (quote: Quote, subscriptions: ProductSubscription[]) {
  let type = EMaintenanceType.Solar;
  switch (quote.type) {
    case EQuoteType.Boiler:
      type = EMaintenanceType.Boiler;
      break;
    case EQuoteType.Solar:
      type = EMaintenanceType.Solar;
      break;
    case EQuoteType.Insulation:
      type = EMaintenanceType.Insulation;
      break;
    case EQuoteType.EvCharger:
      type = EMaintenanceType.EvCharger;
      break;
    case EQuoteType.HeatPump:
      type = EMaintenanceType.HeatPump;
      break;
    case EQuoteType.BoilerSolar:
    case EQuoteType.Other:
      return [];
  }

  return subscriptions.filter((i) => i.maintenanceType === type);
};

export const quoteTypeName = (type: EQuoteType) => {
  switch (type) {
    case EQuoteType.Boiler:
      return "Boiler";
    case EQuoteType.Solar:
      return "Solar PV";
    case EQuoteType.Insulation:
      return "Insulation";
    case EQuoteType.HeatPump:
      return "Heat Pump";
    case EQuoteType.EvCharger:
      return "EV Charger";
    default:
      return "";
  }
};

export const quoteAddonClassifications = function (quote: Quote) {
  if (!hasAddons(quote.addons)) {
    return [];
  }

  return quote
    .addons!.map((i) => i.classification)
    .filter((v, i, a) => a.indexOf(v) === i)
    .filter(notEmpty)
    .sort(addonClassificationSort);
};

export const quoteImage = (quote: Quote): string => {
  return quote.images.length > 0 ? quote.images[0] : "";
};

export const quoteQuantity = function (quote: Quote) {
  return quote.activeItems.length > 0 ? quote.activeItems[0].quantity : 0;
};

export const hasGrant = function (items: QuoteLineItem[]) {
  return hasClassification([EProductClassification.Grants], items);
};

export const hasClassification = function (classification: string[], items: QuoteLineItem[]) {
  return items.some((i) => classification.some((c) => c === i.classification));
};

export const quoteVatRate = (quote: Quote): number => {
  if (quote?.type?.includes("Solar")) {
    if (quote?.purchaserType !== EQuotePurchaserType.Home) {
      return SolarBusinessAgriVatRate;
    }
    return SolarVatRate;
  }
  return BoilerVatRate;
};

export const calculateVatPaid = (postVat: number, vatRate: number): number => {
  vatRate /= 100;
  return (postVat / (1 + vatRate)) * vatRate;
};

export const quoteVat = (quote: Quote | null): number => {
  if (!quote) {
    return 0;
  }

  const productVat = quote.items
    .map((li) => calculateVatPaid(li.price, li.vatRate || 0) * li.quantity)
    .reduce((a, b) => a + b, 0);

  const addonVat = hasAddons(quote.addons)
    ? quote
        .addons!.filter((li) => li.selected)
        .map((li) => calculateVatPaid(li.price, li.vatRate || 0) * li.quantity)
        .reduce((a, b) => a + b, 0)
    : 0;

  const deductionVat = quote.deductions
    .map((li) => calculateVatPaid(Number(li.discountValue), quoteVatRate(quote)))
    .reduce((a, b) => a + b, 0);

  return productVat + addonVat - deductionVat;
};

export const productPriceVat = (price: Price | null): number => {
  return price ? calculateVatPaid(price.price, price.vatRate) : 0;
};

export const quoteDepositAmount = (quote: Quote | null): number => {
  if (!quote) {
    return DepositDefault;
  }

  if (quote.depositRequirement) {
    return quote.depositRequirement;
  }

  if (quote.type === EQuoteType.Boiler) {
    return DepositBoiler;
  }

  var amount = quoteAmountWithOutAddons(quote);

  if (quote.type === EQuoteType.Solar) {
    if (quote.purchaserType === EQuotePurchaserType.Home || amount <= DepositSolarNonDomestic) {
      return DepositSolarDomestic;
    }
    return DepositSolarNonDomestic;
  }

  return DepositDefault;
};

export const deductionValue = (deduction: Deduction): number => {
  if (deduction.type === EDeductionType.Number) {
    return deduction.value * -1;
  }
  return 0;
};

export const quoteDeductionValue = (deduction: QuoteDeduction, includeVat: boolean = true): number => {
  let deductionValue = -1 * (deduction.discountValue ?? 0);

  if (includeVat) {
    return deductionValue;
  }

  return deductionValue / (1 + SolarBusinessAgriVatRate / 100);
};

export const quateAddonQuantityValue = (addon: QuoteLineItem): number => {
  return addon.quantity * addon.price;
};

export const quoteAmountGrant = (quote: Quote): number => {
  return hasGrant(quote.items)
    ? quote
        .addons!.filter((li) => li.classification === EProductClassification.Grants)
        .map((li) => li.price * li.quantity)
        .reduce((a, b) => a + b, 0)
    : 0;
};

export const quoteAmountWithoutGrant = (quote: Quote): number => {
  return quote.price - quoteAmountGrant(quote);
};

export const hasGrantDeductedAtSource = (quote: Quote | null): boolean => {
  return quote?.deductions?.some((x) => x.code === "D0081") ?? false;
};

export const solarGrantAmountAvailable = (quote: Quote | null, solarReport: SolarReport | null): number => {
  const grantAmount = solarReport?.grantAmount ?? 0;
  return hasGrantDeductedAtSource(quote) ? 0 : grantAmount;
};

export const solarGrantAmountForLeadProduct = (product: Product): number => {
  return product?.attributes?.grantAmountAvailable ?? 0;
};

export const quoteAmountWithOutAddons = (quote: Quote): number => {
  return (
    quote.items.map((li) => li.price * li.quantity).reduce((a, b) => a + b, 0) +
    quote.deductions.map((d) => quoteDeductionValue(d)).reduce((a, b) => a + b, 0)
  );
};

export const quotePaymentsMadeValue = (quote: Quote): number => {
  return quotePaymentsMade(quote)
    .map((p) => p.amount)
    .reduce((a, b) => a + b, 0);
};

export const quoteBalanceOutstanding = (quote: Quote): number => {
  return quote.price - quotePaymentsMadeValue(quote);
};

export const quoteInternalPayment = (quote: Quote): Payment | null => {
  return quote.payments?.find((p) => p.type === EPaymentType.Internal) ?? null;
};

export const quotePaymentsMade = (quote: Quote): Payment[] => {
  return (
    quote.payments?.filter((p) => p.status === EPaymentStatus.Paid).sort((a, b) => b.paymentDate - a.paymentDate) ?? []
  );
};

export const allowCustomerEdits = (quote: Quote): boolean => {
  return quote.status === EQuoteStatus.Sent;
};

export const allowManagerEdits = (quote: Quote): boolean => {
  return quote.status === EQuoteStatus.Draft || quote.status === EQuoteStatus.Sent;
};

export const allowAdminEdits = (quote: Quote): boolean => {
  var approvedStatus =
    quote.status === EQuoteStatus.Approved ||
    quote.status === EQuoteStatus.Payment ||
    quote.status === EQuoteStatus.Paid;

  return allowManagerEdits(quote) || (approvedStatus && !quote.xeroInvoiceId && !isLocalHeroes(quote));
};

export const isLocalHeroes = (quote: Quote): boolean => {
  return "Local Heroes" === quote?.leadSource;
};

export const cleanNumberInput = (input: string): string => {
  var value = input.replace(/[^0-9-.]/g, "");

  // remove and duplicate or non-first-character minus
  if (value.indexOf("-") >= 0) {
    value = `-${value.replace("-", "")}`;
  }
  // remove any leading zeros
  while (value.startsWith("0")) {
    value = value.substring(1);
  }
  // Duplicate decimal points
  if (value.indexOf(".") !== value.lastIndexOf(".")) {
    value = value.substring(0, value.indexOf(".") + 1) + value.substring(value.indexOf(".")).replace(/\./g, "");
  }
  // trim to 2 significant bits
  var dotIndex = value.indexOf(".");
  if (dotIndex > 0 && value.length > dotIndex + 3) {
    value = value.substring(0, dotIndex + 3);
  }

  return value;
};

export const categoryTitle = (type: EQuoteType, category: EProductClassification | string): string => {
  if (category === ScheduleCategory) {
    return "Select Your Install Date";
  }

  let typeTitle = "Swyft Boiler Servicing";
  switch (type) {
    case EQuoteType.Boiler:
      typeTitle = "Swyft Boiler Servicing";
      break;
    case EQuoteType.Solar:
      typeTitle = "Swyft Solar Care";
      break;
    case EQuoteType.BoilerSolar:
      typeTitle = "Swyft Solar & Boiler Care";
      break;
  }

  return category === EProductClassification.Maintenance ? typeTitle : "Add " + category;
};

export const categoryDescription = (category: EProductClassification | string): string => {
  const item = EProductClassificationDescriptions.find((i) => {
    return i[0] === category;
  });

  if (category === ScheduleCategory) {
    return "You can select the date you would like your installation to take place";
  }

  return item ? item[1] : "";
};

export const paymentTypeTitle = (paymentType: EFrequencyPaymentType, short: boolean): string => {
  switch (paymentType) {
    case EFrequencyPaymentType.BiWeekly:
      return short ? "p/bw" : "per bi-weekly";
    case EFrequencyPaymentType.Monthly:
      return short ? "p/m" : "per month";
    case EFrequencyPaymentType.Weekly:
      return short ? "p/w" : "per week";
    case EFrequencyPaymentType.Yearly:
      return short ? "p/y" : "per year";
  }
  return "";
};

export const hasArray = <T>(items?: T[] | null): boolean => {
  return (items && items?.length > 0) ?? false;
};

export const hasAddons = (addons?: QuoteLineItem[] | null): boolean => {
  return hasArray(addons);
};

export const hasSubscriptions = (subscriptions?: ProductSubscription[] | null): boolean => {
  return hasArray(subscriptions);
};

export const hasQuoteSubscriptions = (subscriptions?: QuoteSubscription[] | null): boolean => {
  return hasArray(subscriptions);
};

export interface Interval {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  [key: string]: number;
}

export const calculateTimeLeft = (date: Date | null): Interval | null => {
  if (date === null) {
    return null;
  }

  const difference = date.getTime() - new Date().getTime();

  let timeLeft = null;

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  return timeLeft;
};

export const isSlotExpired = (quote: Quote, slot?: EventSlot | null): boolean => {
  if (slot?.status === EEventSlotStatus.Available) {
    return true;
  }

  const difference = calculateTimeLeft(utcToLocal(quote.eventSlotExpire));

  if (difference === null) {
    return true;
  }

  return difference.days === 0 && difference.hours === 0 && difference.minutes === 0 && difference.seconds === 0;
};

export const isSlotExpiredMid = (quote: Quote, slot?: EventSlot | null): boolean => {
  const difference = calculateTimeLeft(utcToLocal(quote.eventSlotExpire));

  if (difference === null) {
    return false;
  }

  return difference.days === 0 && difference.hours === 0 && difference.minutes <= 2 && difference.seconds === 30;
};

export const utcToLocal = (date: Date | null | string | undefined): Date | null => {
  if (!date || date === null) {
    return null;
  }
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (isString(date)) {
    const dateStr = (date as string) ?? "";
    const index = dateStr.indexOf("Z");
    return utcToZonedTime(dateStr.substring(0, index !== -1 ? index : dateStr.length) + "Z", zone);
  }

  return utcToZonedTime(date, zone);
};

export function useIsMounted() {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
}

export const getKeyValue = function <T extends object, U extends keyof T>(obj: T, key: U) {
  return obj[key];
};

export const StringUnion = <UnionType extends string>(...values: UnionType[]) => {
  Object.freeze(values);
  const valueSet: Set<string> = new Set(values);

  const guard = (value: string | any): value is UnionType => {
    return valueSet.has(value);
  };

  const check = (value: string | any): UnionType | undefined => {
    if (!guard(value)) {
      return undefined;
    }
    return value;
  };

  const unionNamespace = { guard, check, values };
  return Object.freeze(unionNamespace as typeof unionNamespace & { type: UnionType });
};
